import { useEffect, useRef, useState, useCallback } from "react";
import { IcPageHeader, IcSelect, IcButton } from "@ukic/react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { api, type Location } from "../api";
import { timeAgo } from "../format";
import { ErrorState } from "../components/ErrorState";

// Colour a point by its source (protocol family). Extend here as sources are added.
function sourceColor(protocol: string | null): string {
  const p = protocol || "";
  if (p.startsWith("iridium.geoloc")) return "#d4351c"; // red  — Iridium terminal estimate
  if (p.startsWith("iridium")) return "#f47738"; //        orange — other Iridium
  if (p === "adsb") return "#1d70b8"; //                    blue — aircraft (ADS-B)
  if (p === "ais") return "#00703c"; //                    green — ships (AIS)
  return "#505a5f"; //                                      grey — other
}

const SOURCE_OPTIONS = [
  { label: "All sources", value: "" },
  { label: "Iridium terminals (geoloc)", value: "iridium.geoloc" },
  { label: "Iridium (all)", value: "iridium." },
  { label: "Aircraft (ADS-B)", value: "adsb" },
  { label: "Ships (AIS)", value: "ais" },
];

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] || c));
}

export default function MapView() {
  const mapEl = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const fittedRef = useRef(false);
  const [locations, setLocations] = useState<Location[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [source, setSource] = useState("");

  // Initialise the Leaflet map once.
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const map = L.map(mapEl.current, { worldCopyJump: true }).setView([30, 0], 2);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 18,
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  const load = useCallback(() => {
    setError(null);
    api
      .listLocations({ protocol: source || undefined, limit: 2000 })
      .then(setLocations)
      .catch(setError);
  }, [source]);

  useEffect(() => {
    fittedRef.current = false; // re-fit when the source filter changes
    load();
    const t = window.setInterval(load, 20_000);
    return () => window.clearInterval(t);
  }, [load]);

  // Re-render markers whenever the location set changes.
  useEffect(() => {
    const lg = layerRef.current;
    if (!lg || !locations) return;
    lg.clearLayers();
    const pts: [number, number][] = [];
    for (const loc of locations) {
      if (typeof loc.lat !== "number" || typeof loc.lon !== "number") continue;
      const color = sourceColor(loc.protocol);
      pts.push([loc.lat, loc.lon]);
      // Error radius (e.g. geoloc estimate) as a faint circle. interactive:false so
      // the ring never intercepts clicks — they pass through to the dots beneath it.
      if (loc.radius_km && loc.radius_km > 0) {
        L.circle([loc.lat, loc.lon], {
          radius: loc.radius_km * 1000,
          color,
          weight: 1,
          fillOpacity: 0.07,
          interactive: false,
        }).addTo(lg);
      }
      const r = loc.radius_km ? ` &plusmn;${loc.radius_km}km` : "";
      L.circleMarker([loc.lat, loc.lon], {
        radius: 6,
        color,
        fillColor: color,
        fillOpacity: 0.9,
        weight: 1,
      })
        .bindPopup(
          `<b>${esc(loc.protocol || loc.format)}</b><br/>` +
            (loc.ident ? `id: <code>${esc(loc.ident)}</code><br/>` : "") +
            `${loc.lat.toFixed(4)}, ${loc.lon.toFixed(4)}${r}<br/>` +
            (loc.label ? `${esc(loc.label)}<br/>` : "") +
            `<small>${esc(loc.device_id)} &middot; ${esc(timeAgo(loc.ts))}</small>`,
        )
        .addTo(lg);
    }
    // Fit to the points once per source selection (don't fight the user's panning).
    if (pts.length && !fittedRef.current && mapRef.current) {
      mapRef.current.fitBounds(L.latLngBounds(pts).pad(0.2), { maxZoom: 9 });
      fittedRef.current = true;
    }
  }, [locations]);

  return (
    <div className="hl-page hl-stack">
      <IcPageHeader
        heading="Map"
        subheading="Located targets across the fleet — Iridium terminal estimates, aircraft, and more"
      >
        <IcSelect
          slot="actions"
          label="Source"
          value={source}
          options={SOURCE_OPTIONS}
          onIcChange={(e) => setSource((e as CustomEvent<{ value: string }>).detail.value)}
        />
        <IcButton slot="actions" variant="secondary" onClick={load}>
          Refresh
        </IcButton>
      </IcPageHeader>

      {error ? <ErrorState error={error} onRetry={load} /> : null}

      <div style={{ position: "relative" }}>
        <div ref={mapEl} style={{ height: "72vh", width: "100%", borderRadius: 4, zIndex: 0 }} />
        <div
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1000,
            background: "white",
            padding: "4px 10px",
            borderRadius: 4,
            fontSize: 13,
            boxShadow: "0 1px 4px rgba(0,0,0,.3)",
          }}
        >
          {locations ? `${locations.length} target(s)` : "loading…"}
        </div>
      </div>
      <p className="hl-muted" style={{ fontSize: 13 }}>
        Positions are estimates. Iridium terminal fixes (red) are beam-center centroids — the radius
        ring is the current error estimate, which tightens with more observations. Map tiles load
        from OpenStreetMap.
      </p>
    </div>
  );
}
